import React, { useState, useEffect, useMemo } from 'react';
import { requestDriveRenameCheck } from '@/components/drive/DriveRenamePrompt';
import { X, AlertTriangle, ExternalLink, Settings, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Combobox from '@/components/Combobox';
import DiscountPicker from '@/components/DiscountPicker';
import { formatNzPhone } from '@/lib/formatters';
import { EmailCheckHint } from '@/components/EmailCheckHint';

const PHONE_KEYS = new Set(['phone_mobile', 'phone_home', 'phone_work']);

// Iter199 — quick-fill email domain chips. Click any chip to append
// the suffix to whatever the operator's already typed in the email
// input. Operator can still type a full custom address.
const EMAIL_DOMAIN_CHIPS = [
  '@gmail.com',
  '@hotmail.com',
  '@outlook.com',
  '@xtra.co.nz',
  '@yahoo.co.nz',
  '@icloud.com',
  '@.co.nz',
  '@.com',
];

const FIELDS = [
  { key: 'name_first', label: 'First Name', required: true },
  { key: 'name_last', label: 'Last Name', required: true },
  // Iter199 — order updated to: Name → Address → Contacts → Email →
  // Cultural Identity → Referral. Operator can still reorder via the
  // ⚙ picker; this is just the default landing order.
  { key: 'address_num', label: 'Street Number' },
  { key: 'address_street', label: 'Street Name' },
  { key: 'address_suburb', label: 'Suburb' },
  { key: 'address_city', label: 'City' },
  { key: 'address_postcode', label: 'Postcode' },
  { key: 'phone_mobile', label: 'Mobile' },
  { key: 'phone_home', label: 'Home Phone' },
  { key: 'phone_work', label: 'Work Phone' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'cultural_identity', label: 'Cultural Identity', type: 'cultural' },
  { key: 'client_referral', label: 'Referral Source', type: 'referral' },
  { key: 'client_comments', label: 'Comments', type: 'textarea' },
];

// Iter197 — operator-controlled field ordering. Persisted to
// localStorage so each operator keeps their preferred typing order.
const FIELD_ORDER_KEY = 'rtr.client_form.field_order.v1';
const DEFAULT_FIELD_ORDER = FIELDS.map(f => f.key);

/**
 * ClientFormModal — shared edit dialog for both ClientsPage (list) and
 * ClientDetailPage. Handles the full client schema:
 *   - Basic identity + contact + address
 *   - Suburb/City type-ahead (with auto-fill)
 *   - First/Last Name type-ahead (Iter197) — ranked by frequency from
 *     the existing client database
 *   - Live duplicate-detection panel (Iter197) — surfaces existing
 *     clients with matching names + existing properties at the same
 *     address, BEFORE the operator hits save.
 *   - ⚙ Field-order picker (Iter197) — drag-free up/down arrows; saved
 *     to localStorage per operator.
 *   - Company toggle + Company Name + Company Type
 *   - Default Discounts checkbox group
 *
 * Props:
 *  - open (boolean)
 *  - onClose () => void
 *  - initial (client doc or empty object)        — pre-fills the form
 *  - onSaved (saved client doc) => void          — called after successful save
 *  - title (string)                              — modal heading; defaults to "New Client"
 */
export default function ClientFormModal({ open, onClose, initial, onSaved, title }) {
  const { api } = useAuth();
  const [form, setForm] = useState(initial || {});
  const [cityOptions, setCityOptions] = useState([]);
  const [suburbOptions, setSuburbOptions] = useState([]);
  const [suburbMeta, setSuburbMeta] = useState([]);
  const [companyTypeOptions, setCompanyTypeOptions] = useState([]);
  const [firstNameOptions, setFirstNameOptions] = useState([]);
  const [lastNameOptions, setLastNameOptions] = useState([]);
  // Iter199 — referral source dropdown + cultural identity options
  const [referralOptions, setReferralOptions] = useState([]);
  const [culturalOptions, setCulturalOptions] = useState([]);
  // Iter200 — region-grouped cities for the new City picker. Default
  // landing city is Hamilton (Waikato region) since RTR is based here.
  const [nzRegions, setNzRegions] = useState([]);
  // Iter199 — streets-in-suburb typeahead. Lazy-loaded each time the
  // suburb changes so the Street Name combobox auto-narrows to known
  // streets in that suburb.
  const [streetsInSuburb, setStreetsInSuburb] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Iter197 — duplicate detection state
  const [clientMatches, setClientMatches] = useState([]);
  const [propertyMatches, setPropertyMatches] = useState([]);
  const [leadMatches, setLeadMatches] = useState([]);
  const [dupesDismissed, setDupesDismissed] = useState(false);

  // Iter197 — field ordering state (persisted)
  const [fieldOrder, setFieldOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(FIELD_ORDER_KEY);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr) && arr.length === DEFAULT_FIELD_ORDER.length && arr.every(k => DEFAULT_FIELD_ORDER.includes(k))) {
        return arr;
      }
    } catch (_) { /* ignore */ }
    return DEFAULT_FIELD_ORDER;
  });
  const [orderPickerOpen, setOrderPickerOpen] = useState(false);
  useEffect(() => {
    try { localStorage.setItem(FIELD_ORDER_KEY, JSON.stringify(fieldOrder)); } catch (_) { /* ignore */ }
  }, [fieldOrder]);

  useEffect(() => {
    if (open) {
      // Iter200 — default new clients to Hamilton (RTR's base city).
      // Only applies on CREATE (no _id); preserves whatever the existing
      // client had on edit.
      const init = initial || {};
      const seeded = (!init._id && !init.address_city)
        ? { ...init, address_city: 'Hamilton' }
        : init;
      setForm(seeded);
      setError('');
      setClientMatches([]);
      setPropertyMatches([]);
      setLeadMatches([]);
      setDupesDismissed(false);
    }
  }, [open, initial]);

  // Fetch dropdowns whenever the modal opens
  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get('/dropdowns/address_cities').catch(() => ({ data: [] })),
      api.get('/dropdowns/address_suburbs').catch(() => ({ data: [] })),
      api.get('/dropdowns/address_suburbs_meta').catch(() => ({ data: [] })),
      api.get('/dropdowns/company_types').catch(() => ({ data: [] })),
      api.get('/dropdowns/first_names_ranked').catch(() => ({ data: [] })),
      api.get('/dropdowns/last_names_ranked').catch(() => ({ data: [] })),
      api.get('/dropdowns/referral_sources').catch(() => ({ data: [] })),
      api.get('/cultural-phrases').catch(() => ({ data: [] })),
      api.get('/dropdowns/nz_regions').catch(() => ({ data: [] })),
    ]).then(([c, s, sm, ct, fn, ln, rs, cu, rg]) => {
      setCityOptions(Array.isArray(c.data) ? c.data : []);
      setSuburbOptions(Array.isArray(s.data) ? s.data : []);
      setSuburbMeta(Array.isArray(sm.data) ? sm.data : []);
      setCompanyTypeOptions(Array.isArray(ct.data) ? ct.data : (ct.data?.options || []));
      setFirstNameOptions(Array.isArray(fn.data) ? fn.data : []);
      setLastNameOptions(Array.isArray(ln.data) ? ln.data : []);
      setReferralOptions(Array.isArray(rs.data) ? rs.data : []);
      setCulturalOptions(Array.isArray(cu.data) ? cu.data : []);
      setNzRegions(Array.isArray(rg.data) ? rg.data : []);
    });
  }, [api, open]);

  // Iter199 — when the suburb changes, lazy-load known streets in
  // that suburb so the Street Name field becomes a typeahead.
  useEffect(() => {
    if (!open) return;
    const sub = (form.address_suburb || '').trim();
    if (!sub) { setStreetsInSuburb([]); return; }
    let alive = true;
    api.get(`/dropdowns/streets-in-suburb?suburb=${encodeURIComponent(sub)}`)
      .then(r => { if (alive) setStreetsInSuburb(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setStreetsInSuburb([]); });
    return () => { alive = false; };
  }, [api, open, form.address_suburb]);

  // Iter197 — Debounced duplicate-detection. Triggers when:
  //   - First+Last name both have ≥2 chars → search /clients AND /leads
  //   - Street No + Street Name both populated → search /properties
  // Skipped when editing an existing client (form._id exists) since
  // self-matches are expected; only the create flow gets the panel.
  useEffect(() => {
    if (!open || form._id || dupesDismissed) {
      setClientMatches([]);
      setPropertyMatches([]);
      setLeadMatches([]);
      return;
    }
    const fn = (form.name_first || '').trim();
    const ln = (form.name_last || '').trim();
    const num = (form.address_num || '').trim();
    const street = (form.address_street || '').trim();
    const haveName = fn.length >= 2 && ln.length >= 2;
    const haveAddr = street.length >= 3;
    if (!haveName && !haveAddr) {
      setClientMatches([]);
      setPropertyMatches([]);
      setLeadMatches([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        if (haveName) {
          // The /clients?search= endpoint matches one substring against
          // any single field — so a "John Smith" query returns 0 hits.
          // Query each token independently and intersect client-side.
          // Iter198 — same trick applied to /leads so telemarketer-
          // entered leads from months ago surface as "did you mean".
          const [c1, c2, l1, l2] = await Promise.all([
            api.get(`/clients?search=${encodeURIComponent(fn)}&limit=20`),
            api.get(`/clients?search=${encodeURIComponent(ln)}&limit=20`),
            api.get(`/leads?search=${encodeURIComponent(fn)}&limit=20`),
            api.get(`/leads?search=${encodeURIComponent(ln)}&limit=20`),
          ]);
          const fnLow = fn.toLowerCase();
          const lnLow = ln.toLowerCase();

          // Clients — union dedup then require both name tokens
          const cA = Array.isArray(c1.data) ? c1.data : (c1.data?.items || []);
          const cB = Array.isArray(c2.data) ? c2.data : (c2.data?.items || []);
          const byCId = {};
          [...cA, ...cB].forEach(c => { if (c?._id) byCId[c._id] = c; });
          const filteredClients = Object.values(byCId).filter(c => {
            const f = (c.name_first || '').toLowerCase();
            const l = (c.name_last || '').toLowerCase();
            const co = (c.company_name || '').toLowerCase();
            const matchFn = f.includes(fnLow) || fnLow.includes(f) || co.includes(fnLow);
            const matchLn = l.includes(lnLow) || lnLow.includes(l) || co.includes(lnLow);
            return matchFn && matchLn;
          }).slice(0, 5);
          setClientMatches(filteredClients);

          // Leads — same idea against contact_first_name + contact_surname
          const lA = Array.isArray(l1.data) ? l1.data : (l1.data?.items || []);
          const lB = Array.isArray(l2.data) ? l2.data : (l2.data?.items || []);
          const byLId = {};
          [...lA, ...lB].forEach(l => { if (l?._id) byLId[l._id] = l; });
          const filteredLeads = Object.values(byLId).filter(l => {
            const f = (l.contact_first_name || '').toLowerCase();
            const s = (l.contact_surname    || '').toLowerCase();
            const matchFn = f.includes(fnLow) || fnLow.includes(f);
            const matchLn = s.includes(lnLow) || lnLow.includes(s);
            return matchFn && matchLn;
          }).slice(0, 5);
          setLeadMatches(filteredLeads);
        } else {
          setClientMatches([]);
          setLeadMatches([]);
        }
        if (haveAddr) {
          const q = encodeURIComponent(`${num} ${street}`.trim());
          const r = await api.get(`/properties?search=${q}&limit=5`);
          const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
          const streetLow = street.toLowerCase();
          const filtered = items.filter(p => {
            const s = (p.property_address_street || '').toLowerCase();
            return s.includes(streetLow) || streetLow.includes(s);
          });
          setPropertyMatches(filtered);
        } else {
          setPropertyMatches([]);
        }
      } catch (_) { /* ignore network errors silently */ }
    }, 400);
    return () => clearTimeout(t);
  }, [api, open, form._id, form.name_first, form.name_last, form.address_num, form.address_street, dupesDismissed]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const id = form._id;
      const payload = { ...form };
      delete payload._id; delete payload.created_at; delete payload.job_count;
      delete payload.last_activity; delete payload.last_status;
      delete payload.lifetime_value; delete payload.properties;
      let saved;
      if (id) {
        const r = await api.put(`/clients/${id}`, payload);
        saved = r.data;
        requestDriveRenameCheck({ client_id: id });
      } else {
        const r = await api.post('/clients', payload);
        saved = r.data;
        // Auto-create a matching Property record on FIRST save when
        // an address was entered, so the new client immediately gets a
        // P# and shows up in the Properties list / property pickers.
        // Skipped on edit (re-running it would duplicate the property).
        const newClientId = saved?._id || saved?.id;
        const streetTrim = (form.address_street || '').trim();
        if (newClientId && streetTrim) {
          try {
            await api.post('/properties', {
              client_id: newClientId,
              property_address_num:    form.address_num    || '',
              property_address_street: streetTrim,
              property_address_suburb: form.address_suburb || '',
              property_address_city:   form.address_city   || '',
            });
          } catch (pe) {
            // Don't block client save on property failure — surface a soft
            // warning instead so the operator can add the property manually.
            console.warn('Auto-property creation failed', pe);
          }
        }
      }
      onSaved && onSaved({ ...form, ...saved, _id: id || saved._id });
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  // Build the ordered field list. Pull each field def from FIELDS by
  // key, dropping any unknown keys defensively.
  const orderedFields = useMemo(() => {
    const byKey = Object.fromEntries(FIELDS.map(f => [f.key, f]));
    return fieldOrder.map(k => byKey[k]).filter(Boolean);
  }, [fieldOrder]);

  const moveField = (key, delta) => {
    setFieldOrder(cur => {
      const idx = cur.indexOf(key);
      if (idx < 0) return cur;
      const next = idx + delta;
      if (next < 0 || next >= cur.length) return cur;
      const out = cur.slice();
      [out[idx], out[next]] = [out[next], out[idx]];
      return out;
    });
  };

  if (!open) return null;

  const hasDupes = !dupesDismissed && (clientMatches.length > 0 || propertyMatches.length > 0 || leadMatches.length > 0);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div data-testid="client-form-modal" className="bg-white rounded-md shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E2E2DF] p-5 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-[#2C2C2C]">{title || (form._id ? 'Edit Client' : 'New Client')}</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="client-form-field-order-btn"
              onClick={() => setOrderPickerOpen(o => !o)}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
              title="Reorder fields"
            >
              <Settings size={12} /> Reorder fields
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
          </div>
        </div>

        {/* Iter197 — Field-order popover */}
        {orderPickerOpen && (
          <div data-testid="client-form-field-order-panel" className="border-b border-[#E2E2DF] bg-slate-50/60 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-600">Field order</p>
              <button
                type="button"
                data-testid="client-form-field-order-reset"
                onClick={() => setFieldOrder(DEFAULT_FIELD_ORDER)}
                className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-slate-500 hover:text-slate-900 hover:bg-white border border-transparent hover:border-slate-200"
              ><RotateCcw size={10} /> Reset</button>
            </div>
            <ul className="space-y-1">
              {orderedFields.map((f, i) => (
                <li key={f.key} className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1 text-xs">
                  <span className="font-mono text-[10px] text-slate-400 w-5 text-right">{i + 1}</span>
                  <span className="flex-1 text-slate-700">{f.label}</span>
                  <button
                    type="button"
                    data-testid={`field-order-up-${f.key}`}
                    onClick={() => moveField(f.key, -1)}
                    disabled={i === 0}
                    className="p-0.5 text-slate-400 hover:text-slate-900 disabled:opacity-30 disabled:cursor-default"
                    title="Move up"
                  ><ArrowUp size={12} /></button>
                  <button
                    type="button"
                    data-testid={`field-order-down-${f.key}`}
                    onClick={() => moveField(f.key, 1)}
                    disabled={i === orderedFields.length - 1}
                    className="p-0.5 text-slate-400 hover:text-slate-900 disabled:opacity-30 disabled:cursor-default"
                    title="Move down"
                  ><ArrowDown size={12} /></button>
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-slate-400 mt-2 italic">Your order is saved on this device and applied to every New Client form.</p>
          </div>
        )}

        <form onSubmit={submit} className="p-5 space-y-5">
          {error && <div data-testid="client-form-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

          {/* Iter197 — Duplicate panel */}
          {hasDupes && (
            <DuplicatePanel
              clientMatches={clientMatches}
              propertyMatches={propertyMatches}
              leadMatches={leadMatches}
              onDismiss={() => setDupesDismissed(true)}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {orderedFields.map(f => {
              const isCity = f.key === 'address_city';
              const isSuburb = f.key === 'address_suburb';
              const isStreet = f.key === 'address_street';
              const isFirst = f.key === 'name_first';
              const isLast = f.key === 'name_last';
              const isEmail = f.type === 'email' || f.key === 'email';
              const isCultural = f.type === 'cultural';
              const isReferral = f.type === 'referral';
              const colSpan = (f.type === 'textarea' || isEmail) ? 'md:col-span-2' : '';
              return (
                <div key={f.key} className={colSpan}>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">{f.label}{f.required && ' *'}</label>
                  {f.type === 'textarea' ? (
                    <textarea data-testid={`client-${f.key}-input`} value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" rows={3} />
                  ) : isFirst ? (
                    <Combobox
                      value={form.name_first || ''}
                      onChange={v => setForm(s => ({ ...s, name_first: v }))}
                      options={firstNameOptions}
                      placeholder="Type a name…"
                      testId="client-name_first-combobox"
                      allowCustom={true}
                    />
                  ) : isLast ? (
                    <Combobox
                      value={form.name_last || ''}
                      onChange={v => setForm(s => ({ ...s, name_last: v }))}
                      options={lastNameOptions}
                      placeholder="Type a surname…"
                      testId="client-name_last-combobox"
                      allowCustom={true}
                    />
                  ) : isCity ? (
                    // Iter200 — city picker grouped by NZ region. Falls
                    // back to the flat alphabetical list if the regions
                    // dropdown failed to load.
                    nzRegions.length > 0 ? (
                      <select
                        data-testid="client-address_city-select"
                        value={form.address_city || ''}
                        onChange={e => setForm({ ...form, address_city: e.target.value })}
                        className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400] bg-white"
                      >
                        <option value="">— pick a city —</option>
                        {nzRegions.map(r => (
                          <optgroup key={r.region} label={r.region}>
                            {r.cities.map(city => (
                              <option key={city} value={city}>{city}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    ) : (
                      <Combobox value={form.address_city || ''} onChange={v => setForm(s => ({ ...s, address_city: v }))} options={cityOptions} placeholder="Hamilton…" testId="client-address_city-combobox" />
                    )
                  ) : isSuburb ? (
                    <div>
                      <Combobox
                        value={form.address_suburb || ''}
                        onChange={async v => {
                          // Iter199/200 — picking a suburb cascades:
                          //   1. Auto-fill city if known and empty
                          //   2. Auto-fill postcode if known and empty
                          //   3. Store the scheduling area_code (A-H)
                          //      from suburb_meta so the Property/Client
                          //      record carries the routing hint.
                          const meta = suburbMeta.find(m => m.name === v);
                          setForm(s => ({
                            ...s,
                            address_suburb: v,
                            address_city: (meta?.city && !s.address_city) ? meta.city : s.address_city,
                            suburb_area_code: meta?.area_code || '',
                          }));
                          if (v) {
                            try {
                              const r = await api.get(`/dropdowns/postcode-for-suburb?suburb=${encodeURIComponent(v)}`);
                              const pc = r?.data?.postcode || '';
                              if (pc) {
                                setForm(s => ({
                                  ...s,
                                  address_postcode: s.address_postcode || pc,
                                }));
                              }
                            } catch (_) { /* ignore */ }
                          }
                        }}
                        options={form.address_city ? [...suburbMeta.filter(m => m.city === form.address_city).map(m => m.name), ...suburbOptions.filter(s => !suburbMeta.find(m => m.name === s && m.city === form.address_city))] : suburbOptions}
                        placeholder="Dinsdale…"
                        testId="client-address_suburb-combobox"
                        hint={(opt) => {
                          const m = suburbMeta.find(x => x.name === opt);
                          if (!m) return null;
                          // Show city + area code (e.g. "Hamilton · Area H")
                          return m.area_code ? `${m.city} · Area ${m.area_code}` : m.city;
                        }}
                      />
                      {form.suburb_area_code && (
                        <p data-testid="client-suburb-area-badge" className="text-[10px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 font-mono">
                          Scheduling Area: <span className="font-bold">{form.suburb_area_code}</span>
                        </p>
                      )}
                    </div>
                  ) : isStreet ? (
                    // Iter199 — Street Name becomes a typeahead of streets
                    // already on file in the selected suburb. If no suburb
                    // is set yet, falls back to a plain input.
                    streetsInSuburb.length > 0 ? (
                      <Combobox
                        value={form.address_street || ''}
                        onChange={v => setForm(s => ({ ...s, address_street: v }))}
                        options={streetsInSuburb}
                        placeholder={`Streets in ${form.address_suburb}…`}
                        testId="client-address_street-combobox"
                        allowCustom={true}
                      />
                    ) : (
                      <input
                        data-testid="client-address_street-input"
                        type="text"
                        value={form.address_street || ''}
                        onChange={e => setForm({ ...form, address_street: e.target.value })}
                        placeholder={form.address_suburb ? `No streets on file for ${form.address_suburb} — type freely` : 'Pick a suburb above to narrow choices…'}
                        className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]"
                      />
                    )
                  ) : isEmail ? (
                    <EmailFieldWithChips
                      value={form.email || ''}
                      onChange={v => setForm({ ...form, email: v })}
                    />
                  ) : isCultural ? (
                    <select
                      data-testid="client-cultural-identity-select"
                      value={form.cultural_identity || ''}
                      onChange={e => setForm({ ...form, cultural_identity: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400] bg-white"
                    >
                      <option value="">— not specified —</option>
                      {culturalOptions.map(o => (
                        <option key={o.key} value={o.key}>{o.label}</option>
                      ))}
                    </select>
                  ) : isReferral ? (
                    <ReferralPicker
                      value={form.client_referral || ''}
                      onChange={v => setForm({ ...form, client_referral: v })}
                      allOptions={referralOptions}
                      onAddCustom={async (name) => {
                        try {
                          const updated = await api.post('/dropdowns/referral_sources/add', { value: name });
                          setReferralOptions(Array.isArray(updated.data) ? updated.data : referralOptions.concat(name));
                        } catch (_) {
                          setReferralOptions(referralOptions.concat(name));
                        }
                      }}
                    />
                  ) : (
                    <input data-testid={`client-${f.key}-input`} type={f.type || 'text'} value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })} onBlur={PHONE_KEYS.has(f.key) ? () => setForm(s => ({ ...s, [f.key]: formatNzPhone(s[f.key] || '') })) : undefined} required={f.required} placeholder={PHONE_KEYS.has(f.key) ? '(07) 855 1234' : undefined} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Company info */}
          <div className="border-t border-[#E2E2DF] pt-4">
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input data-testid="client-is-company-toggle" type="checkbox" checked={!!form.is_company} onChange={e => setForm({ ...form, is_company: e.target.checked, ...(e.target.checked ? {} : { company_name: '', company_type: '' }) })} className="accent-[#D35400] w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">This client is a company</span>
            </label>
            {form.is_company && (
              <div data-testid="company-info-section" className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-[#FFF7ED]/40 border border-[#D35400]/30 rounded-md p-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Company Name *</label>
                  <input data-testid="client-company-name-input" type="text" value={form.company_name || ''} onChange={e => setForm({...form, company_name: e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Company Type</label>
                  <Combobox
                    value={form.company_type || ''}
                    onChange={v => setForm({ ...form, company_type: v })}
                    options={companyTypeOptions}
                    placeholder="Type or pick…"
                    testId="client-company-type-combobox"
                    allowCustom={true}
                    addLabel="Add"
                    onAddNew={async (name) => {
                      const updated = await api.post('/dropdowns/company_types/add', { value: name });
                      setCompanyTypeOptions(Array.isArray(updated.data) ? updated.data : companyTypeOptions.concat(name));
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Default discounts */}
          <div className="border-t border-[#E2E2DF] pt-4">
            <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Default Discounts <span className="font-normal normal-case text-slate-400">— pre-selected on every new appraisal for this client</span></label>
            <DiscountPicker value={form.default_discounts || []} onChange={d => setForm({ ...form, default_discounts: d })} testId="client-default-discount-picker" showTotal={false} />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[#E2E2DF] sticky bottom-0 bg-white">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[#E2E2DF] rounded-md text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
            <button data-testid="save-client-btn" type="submit" disabled={saving} className="px-4 py-2 bg-[#D35400] text-white rounded-md text-sm font-medium hover:bg-[#A04000] disabled:opacity-50">{saving ? 'Saving…' : 'Save Client'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Iter199 — Email input with quick-fill domain chips. Lets the
// operator type just the local part ("john") then click @gmail.com to
// auto-append. Tilde chips (@.co.nz / @.com) drop the operator into a
// "type the company-specific domain" state by leaving the dot exposed.
function EmailFieldWithChips({ value, onChange }) {
  const appendDomain = (chip) => {
    const cur = (value || '').trim();
    const atIdx = cur.indexOf('@');
    const local = atIdx >= 0 ? cur.slice(0, atIdx) : cur;
    // Tilde chips like @.co.nz/@.com leave the operator to fill in the
    // company name between '@' and the suffix. We strip the leading dot
    // and produce 'local@' so the cursor / caret can pick up from there.
    if (chip.startsWith('@.')) {
      const suffix = chip.slice(2); // 'co.nz' or 'com'
      onChange(`${local}@${suffix === 'com' ? '' : ''}${suffix === 'co.nz' ? '' : ''}`.replace(/^([^@]*)@$/, `$1@.${suffix}`));
      // Simpler: just emit local + '@.suffix' so operator can insert company name.
      onChange(`${local}@.${suffix}`);
    } else {
      onChange(`${local}${chip}`);
    }
  };
  return (
    <div>
      <input
        data-testid="client-email-input"
        type="email"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder="name@example.com"
        className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]"
      />
      <EmailCheckHint value={value} onUse={onChange} testId="client-email-check" />
      <div className="flex flex-wrap gap-1 mt-1.5" data-testid="client-email-domain-chips">
        {EMAIL_DOMAIN_CHIPS.map(chip => (
          <button
            key={chip}
            type="button"
            data-testid={`client-email-chip-${chip.replace(/[@.]/g, '-')}`}
            onClick={() => appendDomain(chip)}
            className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-200 text-slate-600 bg-slate-50 hover:bg-[#FFF7ED] hover:border-[#D35400]/40 hover:text-[#A04000] transition-colors"
            title={chip.startsWith('@.') ? `Append ${chip} — fill in the company name between @ and ${chip.slice(1)}` : `Append ${chip}`}
          >{chip}</button>
        ))}
      </div>
    </div>
  );
}

// Iter197 — duplicate detection panel. Non-blocking, dismissible.
function DuplicatePanel({ clientMatches, propertyMatches, leadMatches = [], onDismiss }) {
  const total = clientMatches.length + propertyMatches.length + leadMatches.length;
  return (
    <div
      data-testid="client-form-duplicate-panel"
      className="border border-amber-300 bg-amber-50 rounded-md p-3 space-y-2"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle size={14} className="text-amber-700 flex-shrink-0" />
        <p className="text-sm font-bold text-amber-900 flex-1">
          Possible duplicate{total > 1 ? 's' : ''} already on file — review before saving
        </p>
        <button
          type="button"
          data-testid="client-form-duplicate-dismiss"
          onClick={onDismiss}
          className="text-[10px] uppercase tracking-wider text-amber-700 hover:text-amber-900 border border-amber-300 rounded px-1.5 py-0.5 hover:bg-amber-100"
        >Dismiss</button>
      </div>
      {clientMatches.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800 mb-1">Client name matches ({clientMatches.length})</p>
          <ul className="space-y-1">
            {clientMatches.map(c => {
              const name = c.is_company && c.company_name
                ? c.company_name
                : `${c.name_first || ''} ${c.name_last || ''}`.trim();
              const addr = [c.address_num, c.address_street, c.address_suburb].filter(Boolean).join(' ');
              return (
                <li key={c._id} className="text-xs bg-white border border-amber-200 rounded px-2 py-1.5 flex items-center gap-2">
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold text-slate-800">{name || '(unnamed)'}</span>
                    {c.access_id && <span className="font-mono text-[10px] text-amber-700 ml-1.5">C{c.access_id}</span>}
                    {addr && <span className="text-slate-500 ml-2">· {addr}</span>}
                    {c.email && <span className="text-slate-400 ml-2 truncate">· {c.email}</span>}
                  </span>
                  <a
                    href={`/clients/${c._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`client-form-duplicate-client-${c._id}`}
                    className="text-[11px] font-bold inline-flex items-center gap-0.5 px-2 py-1 rounded border border-amber-400 text-amber-800 bg-white hover:bg-amber-50 flex-shrink-0"
                  >Open <ExternalLink size={10} /></a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {leadMatches.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800 mb-1">
            Lead matches ({leadMatches.length}) <span className="text-amber-600 normal-case font-normal italic">— already captured by telemarketing / events</span>
          </p>
          <ul className="space-y-1">
            {leadMatches.map(l => {
              const name = `${l.contact_first_name || ''} ${l.contact_surname || ''}`.trim();
              const meta = [l.lead_source, l.lead_type, l.event_name].filter(Boolean).join(' · ');
              const created = l.created_at ? String(l.created_at).slice(0, 10) : '';
              return (
                <li key={l._id} className="text-xs bg-white border border-amber-200 rounded px-2 py-1.5 flex items-center gap-2">
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold text-slate-800">{name || '(unnamed lead)'}</span>
                    {l.lead_no && <span className="font-mono text-[10px] text-amber-700 ml-1.5">L{l.lead_no}</span>}
                    {l.contact_suburb && <span className="text-slate-500 ml-2">· {l.contact_suburb}</span>}
                    {meta && <span className="text-slate-400 ml-2 truncate">· {meta}</span>}
                    {created && <span className="text-slate-400 ml-2">· {created}</span>}
                    {l.lead_closed && <span className="ml-2 text-[10px] px-1 rounded bg-slate-100 text-slate-500 uppercase">closed</span>}
                  </span>
                  <a
                    href={`/leads/${l._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`client-form-duplicate-lead-${l._id}`}
                    className="text-[11px] font-bold inline-flex items-center gap-0.5 px-2 py-1 rounded border border-amber-400 text-amber-800 bg-white hover:bg-amber-50 flex-shrink-0"
                  >Open <ExternalLink size={10} /></a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {propertyMatches.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800 mb-1">Property address matches ({propertyMatches.length})</p>
          <ul className="space-y-1">
            {propertyMatches.map(p => {
              const addr = [p.property_address_num, p.property_address_street, p.property_address_suburb].filter(Boolean).join(' ');
              const owner = p.client ? `${p.client.name_first || ''} ${p.client.name_last || ''}`.trim() : (p.client_name || '');
              return (
                <li key={p._id} className="text-xs bg-white border border-amber-200 rounded px-2 py-1.5 flex items-center gap-2">
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold text-slate-800">{addr || '(no address)'}</span>
                    {p.access_id && <span className="font-mono text-[10px] text-amber-700 ml-1.5">P{p.access_id}</span>}
                    {owner && <span className="text-slate-500 ml-2">· owned by {owner}</span>}
                  </span>
                  <a
                    href={`/properties/${p._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`client-form-duplicate-property-${p._id}`}
                    className="text-[11px] font-bold inline-flex items-center gap-0.5 px-2 py-1 rounded border border-amber-400 text-amber-800 bg-white hover:bg-amber-50 flex-shrink-0"
                  >Open <ExternalLink size={10} /></a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <p className="text-[10px] text-amber-700 italic">If this is a NEW client, you can ignore and continue. If it's the same person, open the existing record (or convert the lead) instead of creating a duplicate.</p>
    </div>
  );
}

// Iter201 — Referral picker with a 2-step shortlist UX.
// Step 1: 4 most-common category chips ("Friend", "Advertisement",
// "Realtor referral", "Builder referral") + a "Repeat customer" /
// "Google search" / "Other" row. Click a category to either:
//   (a) Set the value directly (Realtor, Builder, Repeat, Google, Other)
//   (b) Expand into its sub-options (Friend → Neighbour/Work colleague/
//       Relative/Other; Advertisement → Website/Flyers/Fence sign/
//       Newspaper/Radio).
// "More options…" reveals the full long-tail list (incl. operator-added
// custom values), and "Type custom" lets the operator add a brand-new
// referral source on the fly.
function ReferralPicker({ value, onChange, allOptions, onAddCustom }) {
  const [stage, setStage] = useState('root'); // 'root' | 'friend' | 'ad' | 'all' | 'custom'
  const [customDraft, setCustomDraft] = useState('');

  // Reset to root whenever value clears externally
  useEffect(() => {
    if (!value) setStage('root');
  }, [value]);

  const friendSubs = [
    { label: 'Neighbour',        value: 'Friend — Neighbour' },
    { label: 'Work colleague',   value: 'Friend — Work colleague' },
    { label: 'Relative',         value: 'Friend — Relative' },
    { label: 'Other friend',     value: 'Friend — Other' },
  ];
  const adSubs = [
    { label: 'Website',     value: 'Advertisement — Website' },
    { label: 'Flyers',      value: 'Advertisement — Flyers' },
    { label: 'Fence sign',  value: 'Advertisement — Fence sign' },
    { label: 'Newspaper',   value: 'Advertisement — Newspaper' },
    { label: 'Radio',       value: 'Advertisement — Radio' },
  ];

  const pickAndDone = (v) => { onChange(v); setStage('root'); };
  const clear = () => { onChange(''); setStage('root'); };

  // Already chosen — show a chip-style summary with a clear button.
  if (value && stage === 'root') {
    return (
      <div data-testid="client-referral-chosen" className="flex items-center gap-1 px-2 py-1.5 border border-emerald-300 bg-emerald-50 rounded-md">
        <span className="text-sm text-emerald-900 font-semibold flex-1">{value}</span>
        <button
          type="button"
          data-testid="client-referral-change"
          onClick={() => { onChange(''); setStage('root'); }}
          className="text-[10px] uppercase tracking-wider text-emerald-700 hover:text-emerald-900 border border-emerald-300 rounded px-1.5 py-0.5 hover:bg-emerald-100"
        >Change</button>
      </div>
    );
  }

  // STAGE: root — show 4 main category chips + 3 single-step chips
  if (stage === 'root') {
    return (
      <div data-testid="client-referral-picker-root" className="space-y-1.5">
        <div className="flex flex-wrap gap-1.5">
          <Chip testId="referral-cat-friend" label="Friend" onClick={() => setStage('friend')} expand />
          <Chip testId="referral-cat-ad"     label="Advertisement" onClick={() => setStage('ad')} expand />
          <Chip testId="referral-cat-realtor" label="Realtor"      onClick={() => pickAndDone('Realtor referral')} />
          <Chip testId="referral-cat-builder" label="Builder"      onClick={() => pickAndDone('Builder referral')} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Chip testId="referral-cat-repeat" label="Repeat customer" muted onClick={() => pickAndDone('Repeat customer')} />
          <Chip testId="referral-cat-google" label="Google search"  muted onClick={() => pickAndDone('Google search')} />
          <Chip testId="referral-cat-more"   label="More options…"   muted onClick={() => setStage('all')} />
          <Chip testId="referral-cat-custom" label="Type custom…"   muted onClick={() => setStage('custom')} />
        </div>
      </div>
    );
  }

  // STAGE: friend / ad — sub-options + back button
  if (stage === 'friend' || stage === 'ad') {
    const subs = stage === 'friend' ? friendSubs : adSubs;
    const heading = stage === 'friend' ? 'Friend referral' : 'Advertisement';
    return (
      <div data-testid={`client-referral-picker-${stage}`} className="border border-[#D35400]/30 bg-[#FFF7ED]/40 rounded-md p-2 space-y-1.5">
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid={`referral-back-${stage}`}
            onClick={() => setStage('root')}
            className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-900 border border-slate-300 rounded px-1.5 py-0.5 hover:bg-white"
          >‹ Back</button>
          <span className="text-xs font-bold text-[#A04000] uppercase tracking-wider">{heading}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {subs.map(s => (
            <Chip
              key={s.value}
              testId={`referral-sub-${s.value.replace(/\W+/g, '-').toLowerCase()}`}
              label={s.label}
              onClick={() => pickAndDone(s.value)}
            />
          ))}
        </div>
      </div>
    );
  }

  // STAGE: all — full long-tail list
  if (stage === 'all') {
    return (
      <div data-testid="client-referral-picker-all" className="border border-slate-200 bg-white rounded-md p-2 space-y-1.5 max-h-48 overflow-y-auto">
        <div className="flex items-center gap-2 sticky top-0 bg-white pb-1 border-b border-slate-100">
          <button
            type="button"
            onClick={() => setStage('root')}
            className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-900 border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50"
          >‹ Back</button>
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">All referral sources</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {allOptions.map(opt => (
            <button
              key={opt}
              type="button"
              data-testid={`referral-all-${opt.replace(/\W+/g, '-').toLowerCase()}`}
              onClick={() => pickAndDone(opt)}
              className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-700 bg-white hover:bg-[#FFF7ED] hover:border-[#D35400]/40 hover:text-[#A04000] transition-colors"
            >{opt}</button>
          ))}
        </div>
      </div>
    );
  }

  // STAGE: custom — type a brand-new value
  if (stage === 'custom') {
    return (
      <div data-testid="client-referral-picker-custom" className="border border-slate-200 bg-white rounded-md p-2 space-y-1.5">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setStage('root')}
            className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-900 border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50"
          >‹ Back</button>
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Type a new source</span>
        </div>
        <div className="flex items-center gap-1.5">
          <input
            data-testid="client-referral-custom-input"
            value={customDraft}
            onChange={e => setCustomDraft(e.target.value)}
            placeholder="e.g. Plumber recommendation"
            className="flex-1 px-2 py-1 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]"
            autoFocus
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && customDraft.trim()) {
                e.preventDefault();
                const v = customDraft.trim();
                if (onAddCustom) await onAddCustom(v);
                pickAndDone(v);
                setCustomDraft('');
              }
            }}
          />
          <button
            type="button"
            data-testid="client-referral-custom-save"
            onClick={async () => {
              const v = customDraft.trim();
              if (!v) return;
              if (onAddCustom) await onAddCustom(v);
              pickAndDone(v);
              setCustomDraft('');
            }}
            disabled={!customDraft.trim()}
            className="text-[11px] font-bold px-2 py-1 rounded bg-[#D35400] text-white hover:bg-[#A04000] disabled:opacity-40"
          >Add & select</button>
        </div>
      </div>
    );
  }

  return null;
}

function Chip({ label, onClick, expand, muted, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${
        muted
          ? 'border-slate-200 text-slate-500 bg-white hover:bg-slate-50 hover:text-slate-800'
          : 'border-[#D35400]/40 text-[#A04000] bg-white hover:bg-[#FFF7ED]'
      }`}
    >
      {label}{expand && <span className="ml-1 text-slate-400">›</span>}
    </button>
  );
}
